import { intlLocale } from "./date"

/**
 * Formats a raw percent value (already in percent units — 0.1 means "0.1%",
 * not a 0-1 fraction) for display: trims trailing zeros and renders with the
 * app language's own decimal separator (e.g. "0,1" for German).
 */
export function formatPct(value: number, language: string): string {
  return new Intl.NumberFormat(intlLocale(language), {
    maximumFractionDigits: 4,
    useGrouping: false,
  }).format(value)
}

// UN/ECE Recommendation 20 codes as ERP exports carry them. A code not listed
// here is shown as it came in.
const UNIT_SYMBOLS: Record<string, { DE: string; EN: string }> = {
  PCE: { DE: "Stk", EN: "pcs" },
  MTR: { DE: "m", EN: "m" },
  MLT: { DE: "ml", EN: "ml" },
  KGM: { DE: "kg", EN: "kg" },
  GRM: { DE: "g", EN: "g" },
  LTR: { DE: "l", EN: "l" },
}

/** Display symbol for a unit code ("PCE" -> "Stk" in German); unknown codes pass through. */
export function formatUnit(unit: string, language: string): string {
  const symbols = UNIT_SYMBOLS[unit.trim().toUpperCase()]
  if (!symbols) return unit
  return language === "DE" ? symbols.DE : symbols.EN
}

/** A quantity with its unit, using the app language's decimal separator ("0,47 m" in German). */
export function formatQuantity(
  quantity: number,
  unit: string | null | undefined,
  language: string,
): string {
  const value = new Intl.NumberFormat(intlLocale(language), {
    maximumFractionDigits: 4,
  }).format(quantity)
  return unit ? `${value} ${formatUnit(unit, language)}` : value
}
